import { call, put, all, takeLatest, select } from 'redux-saga/effects';
import { IAddNewCard, ICreatePaymentIntent, IGetPublishableKey, types } from './types';
import {
    addPaymentMethodFail,
    addPaymentMethodSuccess,
    createPaymentIntentFail,
    createPaymentIntentForPurchaseSuccess,
    createPaymentIntentForPurchasetFail,
    createPaymentIntentSuccess,
    deletePaymentMethodFail,
    deletePaymentMethodSuccess,
    getPaymentMethodsFail,
    getPaymentMethodsSuccess,
    getPublishableKeyFail,
    getPublishableKeySuccess,
    updatePaymentMethodFail,
    updatePaymentMethodSuccess,
} from './action';
import { globalErrorHandler } from '../error/saga';
import { Action } from 'redux';
import { api } from '../../configurations/api';
import {
    addNewPaymentMethodUrl,
    addPaymentMethodUrl,
    createPaymentIntentForSinglePurchaseUrl,
    createPaymentIntentUrl,
    getMyPaymentMethodsUrl,
    getPublishableKeyUrl,
    paymentMethodUrl,
} from '../../configurations/api/url';
import { httpRequest } from '../types';
import { SentryCapture } from '../../analytics/Sentry';
import { getCacheByType, getCacheDuration, isUseCacheEnabled } from '../cache/saga';
import { CACHE_TYPE, CacheValue } from '../cache/types';
import { showToastMessage } from '../../utils/AppUtils';
import { IGenericDocumentModifier } from '../../types/global/helper';
import { RootState } from '../root-reducer';
import { updateUserConfig } from '../configuration/action';

function* createPaymentIntent({
    payload,
    resolve,
    reject,
}: {
    payload: ICreatePaymentIntent;
    resolve: any;
    reject: any;
}): Generator<any, any, any> {
    try {
        const response = yield call(api, createPaymentIntentUrl, httpRequest.POST, payload, 3, 100);
        const { clientSecret } = response;
        yield put(createPaymentIntentSuccess(clientSecret));
        if (resolve) resolve(clientSecret);
    } catch (error: any) {
        if (reject) reject(error);
        yield put(createPaymentIntentFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* createPaymentIntentForPurchase({
    payload,
    resolve,
    reject,
}: {
    payload: ICreatePaymentIntent;
    resolve: any;
    reject: any;
}): Generator<any, any, any> {
    try {
        const response = yield call(api, createPaymentIntentForSinglePurchaseUrl, httpRequest.POST, payload, 3, 100);
        const { clientSecret } = response;
        yield put(createPaymentIntentForPurchaseSuccess(clientSecret));
        if (resolve) resolve(clientSecret);
    } catch (error: any) {
        if (reject) reject(error);
        yield put(createPaymentIntentForPurchasetFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getPublishableKey({ payload }: { payload: IGetPublishableKey }): any {
    try {
        const response = yield call(
            api,
            `${getPublishableKeyUrl}?source=${payload.source}`,
            httpRequest.GET,
            null,
            2,
            1000,
        );
        const { publishableKey, isNonUs } = response;
        yield put(getPublishableKeySuccess({ isNonUs, publishableKey }));
    } catch (error: any) {
        yield put(getPublishableKeyFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getPaymentMethods(): any {
    const defaultUseCache = yield* isUseCacheEnabled();
    const defaultCacheDuration = yield* getCacheDuration();
    let initialResult: any = null;
    const cache: CacheValue = yield* getCacheByType(CACHE_TYPE.PAYMENT);

    if (
        !!initialResult &&
        defaultUseCache &&
        ((Date.now() - Number(cache.key)) as unknown as number) < defaultCacheDuration
    ) {
        yield put(getPaymentMethodsSuccess(initialResult));
    } else {
        try {
            const response = yield call(api, getMyPaymentMethodsUrl, httpRequest.GET, {}, 0, 0);
            const { data } = response.data;
            yield put(getPaymentMethodsSuccess(data));
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getPaymentMethodsFail(error));
            yield call(globalErrorHandler, error);
        }
    }
}

function* addPaymenMethods({ payload, resolve, reject }: { payload: IAddNewCard; resolve: any; reject: any }): any {
    const request = false ? addNewPaymentMethodUrl : addPaymentMethodUrl;
    try {
        const response = yield call(api, request, httpRequest.POST, payload, 0, 0);
        const { data } = response;
        const { charge, authorization, refund } = data || {};
        if (authorization && charge && refund) {
            yield put(addPaymentMethodSuccess(authorization.data));
            if (refund) {
                yield call(showToastMessage, charge.message);
                yield call(showToastMessage, refund.message);
            }
            const state: RootState = yield select();
            const { paymentMethods } = state.payment;
            if (paymentMethods?.length === 1) {
                yield put(
                    updateUserConfig({
                        payload: {
                            default_payment_method: authorization.data._id,
                        },
                    }),
                );
            }
        } else {
            const state: RootState = yield select();
            const { paymentMethods } = state.payment;
            yield put(addPaymentMethodSuccess(data));
            if (paymentMethods?.length === 1) {
                yield put(
                    updateUserConfig({
                        payload: {
                            default_payment_method: data._id,
                        },
                    }),
                );
            }
        }
        /** Check if this is the only card the user had and update preference */

        yield call(showToastMessage, 'New Card Added Successfully', 'success');
        if (resolve) resolve(data);
    } catch (error: any) {
        if (reject) reject(error);
        yield call(showToastMessage, error);
        yield put(addPaymentMethodFail(error));
    }
}

function* updatePaymenMethod({ payload }: { payload: any }): any {
    try {
        const response = yield call(api, addPaymentMethodUrl, httpRequest.GET, payload, 0, 0);
        const { data } = response.data;
        yield put(updatePaymentMethodSuccess(data));
    } catch (error: any) {
        yield call(showToastMessage, error);
        yield put(updatePaymentMethodFail(error));
    }
}

function* deletePaymenMethod({ payload }: { payload: IGenericDocumentModifier }): any {
    try {
        yield call(api, `${paymentMethodUrl}${payload.documentId}`, httpRequest.DELETE, payload, 0, 0);
        yield put(deletePaymentMethodSuccess(payload));
    } catch (error: any) {
        yield call(showToastMessage, error);
        yield put(deletePaymentMethodFail(error));
    }
}

interface TaskAction extends Action {
    type: string;
    payload: any;
    reject: any;
    resolve: any;
}

function* getPaymentMethodsWatcher() {
    yield takeLatest<TaskAction>(types.GET_PAYMENT_METHODS, getPaymentMethods);
}

function* addPaymenMethodsWatcher() {
    yield takeLatest<TaskAction>(types.ADD_PAYMENT_METHOD, addPaymenMethods);
}

function* updatePaymenMethodsWatcher() {
    yield takeLatest<TaskAction>(types.UPDATE_PAYMENT_METHOD, updatePaymenMethod);
}

function* deletePaymenMethodWatcher() {
    yield takeLatest<TaskAction>(types.DELETE_PAYMENT_METHOD, deletePaymenMethod);
}

function* createPaymentIntentWatcher() {
    yield takeLatest<TaskAction>(types.CREATE_PAYMENT_INTENT, createPaymentIntent);
}

function* createPaymentIntentForPurchaseWatcher() {
    yield takeLatest<TaskAction>(types.CREATE_PAYMENT_INTENT_FOR_PURCHASE, createPaymentIntentForPurchase);
}

function* getPublishableKeyWatcher() {
    yield takeLatest<TaskAction>(types.GET_PUBLISHABLE_KEY, getPublishableKey);
}

export default function* paymentSaga() {
    yield all([
        getPaymentMethodsWatcher(),
        addPaymenMethodsWatcher(),
        updatePaymenMethodsWatcher(),
        deletePaymenMethodWatcher(),
        getPublishableKeyWatcher(),
        createPaymentIntentWatcher(),
        createPaymentIntentForPurchaseWatcher(),
    ]);
}
